import {computed, type Ref} from "vue";
import {useQuery, useMutation, useQueryClient} from "@tanstack/vue-query";
import {fetchWrapper} from "@/helpers/fetch-wrapper";
import {useSessionStore} from "@/stores/session-store";
import {subscriptionsApi} from "@/services/subscriptions";
import type {ApiError} from "@/helpers/api-error";
import type {Product, ProductVersionSummary} from "@/types";

const BASE = `${import.meta.env.VITE_API_URL}/products`;

// READ — publisher products list
export function useProducts() {
    const {data, isFetching: productsLoading, error: productsError} = useQuery({
        queryKey: ["products"],
        queryFn: () => fetchWrapper.get<Product[]>(BASE),
    });
    const products = computed(() => data.value ?? []);

    return {products, productsLoading, productsError: productsError as Ref<ApiError | null>};
}

// READ — subscribed products (requires orgId from auth)
export function useSubscribedProducts() {
    const sessionStore = useSessionStore();
    const orgId = computed(() => sessionStore.session?.currentOrg?.orgId as string | undefined);

    const {data, isFetching: subscribedProductsLoading, error: subscribedProductsError} = useQuery({
        queryKey: ["subscribed-products", orgId],
        queryFn: () => subscriptionsApi.getOrgSubscribedProducts(orgId.value!),
        enabled: computed(() => !!orgId.value),
    });
    const subscribedProducts = computed(() => data.value ?? []);

    return {subscribedProducts, subscribedProductsLoading, subscribedProductsError};
}

// READ — single product
export function useProduct(id: Ref<string>) {
    const {data: product, isFetching: productLoading, error: productError} = useQuery({
        queryKey: ["product", id],
        queryFn: () => fetchWrapper.get<Product>(`${BASE}/${id.value}`),
        enabled: computed(() => !!id.value),
    });

    return {product, productLoading, productError: productError as Ref<ApiError | null>};
}

// WRITE — create
export function useCreateProduct() {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        Product,
        ApiError,
        Partial<Product>
    >({
        mutationFn: (payload: Partial<Product>) => fetchWrapper.post<Product>(BASE, payload),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["products"]}),
    });

    return {
        createProduct: mutate,
        createProductAsync: mutateAsync,
        createProductPending: isPending,
        createProductError: error as Ref<ApiError | null>,
        createProductReset: reset,
    };
}

// WRITE — update
export function useUpdateProduct() {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        Product,
        ApiError,
        Partial<Product> & { id: string }
    >({
        mutationFn: ({id, ...payload}: Partial<Product> & { id: string }) =>
            fetchWrapper.put<Product>(`${BASE}/${id}`, payload),
        onSuccess: (_data, {id}) => {
            queryClient.invalidateQueries({queryKey: ["products"]});
            queryClient.invalidateQueries({queryKey: ["product", id]});
        },
    });

    return {
        updateProduct: mutate,
        updateProductAsync: mutateAsync,
        updateProductPending: isPending,
        updateProductError: error as Ref<ApiError | null>,
        updateProductReset: reset,
    };
}

// WRITE — delete
export function useDeleteProduct() {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        string
    >({
        mutationFn: (id: string) => fetchWrapper.delete(`${BASE}/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["products"]});
            // The 1:1 master project is soft-deleted alongside the product — refresh project lists.
            queryClient.invalidateQueries({queryKey: ["projects"]});
        },
    });

    return {
        deleteProduct: mutate,
        deleteProductAsync: mutateAsync,
        deleteProductPending: isPending,
        deleteProductError: error as Ref<ApiError | null>,
        deleteProductReset: reset,
    };
}

// --- Version management ---

// READ — list versions for a product
export function useProductVersions(productId: Ref<string>) {
    const {data, isFetching: versionsLoading, error: versionsError} = useQuery({
        queryKey: computed(() => ["product-versions", productId.value]),
        queryFn: () => fetchWrapper.get<ProductVersionSummary[]>(`${BASE}/${productId.value}/versions`),
        enabled: computed(() => !!productId.value),
    });
    const versions = computed(() => data.value ?? []);

    return {versions, versionsLoading, versionsError: versionsError as Ref<ApiError | null>};
}

// WRITE — create a new product version
export function useCreateProductVersion(productId: Ref<string>) {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        ProductVersionSummary,
        ApiError,
        // selectedWorkflowIds omitted = every published workflow (backend default); a list = the subset
        // this version should contain.
        { versionNumber: string; description: string; releaseNotes?: string; selectedWorkflowIds?: string[] }
    >({
        mutationFn: (payload) =>
            fetchWrapper.post<ProductVersionSummary>(`${BASE}/${productId.value}/versions`, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["product-versions", productId.value]});
        },
    });

    return {
        createVersion: mutate,
        createVersionAsync: mutateAsync,
        createVersionPending: isPending,
        createVersionError: error as Ref<ApiError | null>,
        createVersionReset: reset,
    };
}

// WRITE — publish a version (from Draft or Archived). The previously-Published version (if any)
// is auto-archived by the backend. Subscribers auto-sync to the new published version.
export function usePublishProductVersion(productId: Ref<string>) {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        string
    >({
        mutationFn: (versionId: string) =>
            fetchWrapper.put(`${BASE}/${productId.value}/versions/${versionId}/publish`),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["product-versions", productId.value]});
        },
    });

    return {
        publishVersion: mutate,
        publishVersionAsync: mutateAsync,
        publishVersionPending: isPending,
        publishVersionError: error as Ref<ApiError | null>,
        publishVersionReset: reset,
    };
}

// --- Preview track ---

export interface PreviewCandidate {
    orgId: string;
    orgName: string;
    isPublisherOrg: boolean;
    isCurrentlySelected: boolean;
}

// READ — orgs that can be selected as Preview subscribers (all active subscribers + the publisher org)
export function useProductPreviewCandidates(productId: Ref<string>, enabled: Ref<boolean>) {
    const {data, isFetching: previewCandidatesLoading, error: previewCandidatesError} = useQuery({
        queryKey: computed(() => ["product-preview-candidates", productId.value]),
        queryFn: () => fetchWrapper.get<PreviewCandidate[]>(`${BASE}/${productId.value}/preview-candidates`),
        enabled: computed(() => enabled.value && !!productId.value),
    });
    const previewCandidates = computed(() => data.value ?? []);
    return {previewCandidates, previewCandidatesLoading, previewCandidatesError: previewCandidatesError as Ref<ApiError | null>};
}

// WRITE — publish a Draft to a chosen subset of subscriber orgs as a Preview.
export function usePublishProductVersionAsPreview(productId: Ref<string>) {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        { versionId: string; subscriberOrgIds: string[] }
    >({
        mutationFn: ({versionId, subscriberOrgIds}) =>
            fetchWrapper.post(
                `${BASE}/${productId.value}/versions/${versionId}/publish-preview`,
                {subscriberOrgIds}),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["product-versions", productId.value]});
            queryClient.invalidateQueries({queryKey: ["product-preview-candidates", productId.value]});
        },
    });

    return {
        publishAsPreview: mutate,
        publishAsPreviewAsync: mutateAsync,
        publishAsPreviewPending: isPending,
        publishAsPreviewError: error as Ref<ApiError | null>,
        publishAsPreviewReset: reset,
    };
}

// WRITE — replace the preview-subscriber list on an active Preview version.
export function useUpdatePreviewSubscribers(productId: Ref<string>) {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        { versionId: string; subscriberOrgIds: string[] }
    >({
        mutationFn: ({versionId, subscriberOrgIds}) =>
            fetchWrapper.put(
                `${BASE}/${productId.value}/versions/${versionId}/preview-subscribers`,
                {subscriberOrgIds}),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["product-versions", productId.value]});
            queryClient.invalidateQueries({queryKey: ["product-preview-candidates", productId.value]});
        },
    });

    return {
        updatePreviewSubscribers: mutate,
        updatePreviewSubscribersAsync: mutateAsync,
        updatePreviewSubscribersPending: isPending,
        updatePreviewSubscribersError: error as Ref<ApiError | null>,
        updatePreviewSubscribersReset: reset,
    };
}

// WRITE — end an active Preview. Archives the version, reverts preview-subscribers to Published.
export function useEndPreview(productId: Ref<string>) {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        string
    >({
        mutationFn: (versionId: string) =>
            fetchWrapper.post(`${BASE}/${productId.value}/versions/${versionId}/end-preview`),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["product-versions", productId.value]});
            queryClient.invalidateQueries({queryKey: ["product-preview-candidates", productId.value]});
        },
    });

    return {
        endPreview: mutate,
        endPreviewAsync: mutateAsync,
        endPreviewPending: isPending,
        endPreviewError: error as Ref<ApiError | null>,
        endPreviewReset: reset,
    };
}

// WRITE — archive a version
export function useArchiveProductVersion(productId: Ref<string>) {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        string
    >({
        mutationFn: (versionId: string) =>
            fetchWrapper.put(`${BASE}/${productId.value}/versions/${versionId}/archive`),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["product-versions", productId.value]});
        },
    });

    return {
        archiveVersion: mutate,
        archiveVersionAsync: mutateAsync,
        archiveVersionPending: isPending,
        archiveVersionError: error as Ref<ApiError | null>,
        archiveVersionReset: reset,
    };
}

// WRITE — delete a draft version
export function useDeleteProductVersion(productId: Ref<string>) {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        string
    >({
        mutationFn: (versionId: string) =>
            fetchWrapper.delete(`${BASE}/${productId.value}/versions/${versionId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["product-versions", productId.value]});
        },
    });

    return {
        deleteVersion: mutate,
        deleteVersionAsync: mutateAsync,
        deleteVersionPending: isPending,
        deleteVersionError: error as Ref<ApiError | null>,
        deleteVersionReset: reset,
    };
}

// --- Components (roll-up of what the product exposes to subscribers) ---

export interface ProductServiceComponent {
    serviceId: string;
    serviceName: string;
    sourceWorkflowId: string;
    sourceWorkflowName: string;
    serviceModelType: string;
}

export interface ProductConnectionComponent {
    slotName: string;
    connectionTemplateId: string;
}

export interface ProductDatastoreComponent {
    name: string;
    description: string;
}

export interface ProductComponents {
    productId: string;
    productVersionId: string;
    services: ProductServiceComponent[];
    connections: ProductConnectionComponent[];
    datastores: ProductDatastoreComponent[];
}

// READ — publisher-product metrics for the org's products.
// `subscriberCount`, `successRate` and `totalRuns` are derived from real data (see
// GetPublisherProductsHandler — success rate tallied from RunHistory outcomes across the master
// project's workflows). `monthlyRevenue` is still backend-stubbed (subscriberCount × $50) because
// we have no billing model yet — the UI greys that one out.
// `totalRuns === 0` is the signal for "no runs yet, don't render a 0% rate."
export interface PublisherProductMetric {
    id: string;
    name: string;
    version: string;
    status: string;
    subscriberCount: number;
    /** Count of workflows in the product's master project. */
    workflowCount: number;
    successRate: number;
    totalRuns: number;
    monthlyRevenue: number;
}

export function usePublisherProductMetrics(orgId: Ref<string>) {
    const ORGS_BASE = `${import.meta.env.VITE_API_URL}/orgs`;
    const {data, isFetching, error} = useQuery({
        queryKey: computed(() => ["publisher-product-metrics", orgId.value]),
        queryFn: () => fetchWrapper.get<PublisherProductMetric[]>(`${ORGS_BASE}/${orgId.value}/publisher-products`),
        enabled: computed(() => !!orgId.value),
    });
    const metricsByProduct = computed(() => {
        const map = new Map<string, PublisherProductMetric>();
        for (const m of data.value ?? []) map.set(m.id, m);
        return map;
    });
    return {
        metricsByProduct,
        metricsLoading: isFetching,
        metricsError: error as Ref<ApiError | null>,
    };
}

// READ — components exposed by the product's currently-published version
export function useProductComponents(productId: Ref<string>, enabled: Ref<boolean>) {
    const {data, isFetching: productComponentsLoading, error: productComponentsError} = useQuery({
        queryKey: computed(() => ["product-components", productId.value]),
        queryFn: () => fetchWrapper.get<ProductComponents>(`${BASE}/${productId.value}/components`),
        enabled: computed(() => enabled.value && !!productId.value),
    });
    const productComponents = computed(() => data.value ?? null);

    return {
        productComponents,
        productComponentsLoading,
        productComponentsError: productComponentsError as Ref<ApiError | null>,
    };
}

// UTILITY — invalidate from outside
export function useInvalidateProducts() {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({queryKey: ["products"]});
}
